
// src/components/DefinitionText.js
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { getLockedDefinitionKeys } from '../data/tierConfig';
import definitionPlaceholders from '../definitionPlaceholders.json';

/* ── Journey-level metadata ─────────────────────────────────── */
const journeyLevels = [
  {
    level: 1,
    icon: '📄',
    title: 'Financial Statements',
    subtitle: 'The raw building blocks — the numbers a company reports each quarter.',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    badge: 'bg-blue-600',
    keys: [
      'totalRevenue', 'grossProfit', 'grossProfitRatio', 'ebitda', 'ebitdaRatio',
      'incomeBeforeTax', 'incomeBeforeTaxRatio', 'netIncome', 'netIncomeRatio',
      'operatingIncome', 'operatingIncomeRatio', 'earningsPerShare',
      'totalAssets', 'totalCurrentAssets', 'cashAndCashEquivalents', 'cashAndEquivalents',
      'netReceivables', 'inventory', 'retainedEarnings', 'totalEquity',
      'totalLiabilities', 'totalCurrentLiabilities', 'accountPayable',
      'totalDebt', 'netDebt', 'commonStock', 'nonCurrentAssets',
      'operatingCashFlow', 'capitalExpenditure', 'freeCashFlow', 'workingCapital',
      'acquisitions', 'changeInWorkingCapital', 'netChangeInCash',
      'cashAtBeginningOfPeriod', 'cashAtEndOfPeriod', 'stockBasedCompensation',
      'commonStockIssuance', 'commonStockRepurchased', 'dividendsPaid', 'debtRepayment',
    ],
  },
  {
    level: 2,
    icon: '📐',
    title: 'Ratios & Advanced Metrics',
    subtitle: 'Normalised measures that let you compare companies of any size on equal footing.',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    badge: 'bg-emerald-600',
    keys: [
      'nopat', 'nopatMarginPct', 'grossMarginPct', 'ebitdaMarginPct',
      'netProfitMarginPct', 'grossProfitRatioPct', 'operatingIncomeRatioPct',
      'assetTurnover', 'inventoryTurnover', 'dsoDays', 'dioDays', 'dpoDays', 'cccDays',
      'revenueGrowthPct', 'revenueGrowth', 'netIncomeGrowthPct', 'netIncomeGrowth',
      'operatingIncomeGrowth', 'epsGrowthPct',
      'currentRatio', 'quickRatio', 'operatingCashflowRatio', 'operatingCashFlowRatio',
      'debtToEquity', 'debtToAssets', 'netDebtToEbitda', 'equityMultiplier',
      'interestCoverage', 'interestCoverageRatio', 'altmanZScore',
      'financialLeverage', 'fixedChargeCoverage',
      'roePct', 'roaPct', 'roicPct', 'rocePct',
      'freeCashFlowMarginPct', 'fcfYieldPct', 'fcfSalesPct', 'fcfToSalesPct',
      'sustainableGrowthRatePct', 'opCashFlowMarginPct', 'operatingCashFlowMarginPct',
      'cfoToNetIncomeRatio',
      'dividendPayoutRatioPct', 'dividendPayoutPct',
      'economicValueAdded', 'capexCoverage', 'rndEfficiency',
      'maintainableFcf', 'maintainable_fcf',
    ],
  },
  {
    level: 3,
    icon: '🔍',
    title: 'Earnings Quality',
    subtitle: 'Signals that test whether reported profits are backed by real cash.',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    badge: 'bg-amber-600',
    keys: [
      'accrualsRatio', 'cfoToNetIncome', 'sloanRatio', 'dsrIndex', 'earningsQualityScore',
    ],
  },
  {
    level: 4,
    icon: '💎',
    title: 'Valuation',
    subtitle: 'Multiples that compare what the market is paying to what the business produces.',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
    badge: 'bg-indigo-600',
    keys: [
      'peRatio', 'pbRatio', 'evToEbitda',
      'dividendYieldPct', 'buybackYieldPct',
      'revenuePerShare', 'bookValuePerShare', 'tangibleBookValuePerShare',
    ],
  },
  {
    level: 5,
    icon: '📈',
    title: 'Visualisations & Trends',
    subtitle: 'Multi‑quarter charts that turn numbers into narratives — see the trend, not just the data point.',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    badge: 'bg-purple-600',
    keys: [
      'vizReturnsOnCapital', 'vizProfitabilityCascade', 'vizGrowthTrajectoryPulse',
      'vizFcfVsNetIncome', 'vizDebtCoverageProfile', 'vizEfficiency',
      'vizLiquiditySolvency', 'vizEquityBookValue', 'vizCfoStrength', 'vizCashFlowAllocation',
    ],
  },
];

/* ── Pretty label ───────────────────────────────────────────── */
const toPrettyLabel = (key) =>
  String(key || '')
    .replace(/^viz/, '')
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/\bFcf\b/g, 'FCF')
    .replace(/\bEps\b/g, 'EPS')
    .replace(/\bEbitda\b/g, 'EBITDA')
    .replace(/\bCfo\b/g, 'CFO')
    .replace(/\bR&D\b/g, 'R&D');

/* ── Build a key→level lookup ───────────────────────────────── */
const keyToLevel = {};
journeyLevels.forEach((jl) => {
  jl.keys.forEach((k) => { keyToLevel[k] = jl; });
});

/* ── Badge component ────────────────────────────────────────── */
const LevelBadge = ({ level }) => {
  const jl = journeyLevels.find((j) => j.level === level);
  if (!jl) return null;
  return (
    <span
      className={`inline-flex items-center justify-center text-[10px] font-bold text-white rounded-full w-5 h-5 ${jl.badge} flex-shrink-0`}
      title={`Level ${jl.level}: ${jl.title}`}
    >
      L{jl.level}
    </span>
  );
};

/* ── Main Component ─────────────────────────────────────────── */
const DefinitionText = ({
  selectedCompany,
  selectedQuarter,
  definitions,
  maxWidthClass = 'max-w-5xl',
  showContextHeader = true,
}) => {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const { userPlanTier } = useUser();
  const navigate = useNavigate();
  const lockedKeys = useMemo(() => getLockedDefinitionKeys(userPlanTier), [userPlanTier]);

  const sourceDefs = useMemo(() => {
    if (definitions && Object.keys(definitions).length > 0) return definitions;
    return definitionPlaceholders;
  }, [definitions]);

  const toggle = (level) =>
    setCollapsed((prev) => ({ ...prev, [level]: !prev[level] }));

  const searchLower = search.toLowerCase().trim();

  /* Build grouped data: each level's definitions filtered by search */
  const grouped = useMemo(() => {
    return journeyLevels.map((jl) => {
      const items = jl.keys
        .filter((k) => sourceDefs[k])
        .map((k) => ({ key: k, label: toPrettyLabel(k), text: sourceDefs[k] }))
        .filter((item) => {
          if (!searchLower) return true;
          return (
            item.label.toLowerCase().includes(searchLower) ||
            item.text.toLowerCase().includes(searchLower)
          );
        });
      return { ...jl, items };
    });
  }, [sourceDefs, searchLower]);

  /* Uncategorised keys (any new definitions not yet mapped to a level) */
  const allMappedKeys = new Set(journeyLevels.flatMap((jl) => jl.keys));
  const uncategorised = useMemo(() => {
    return Object.keys(sourceDefs)
      .filter((k) => !allMappedKeys.has(k))
      .map((k) => ({ key: k, label: toPrettyLabel(k), text: sourceDefs[k] }))
      .filter((item) => {
        if (!searchLower) return true;
        return (
          item.label.toLowerCase().includes(searchLower) ||
          item.text.toLowerCase().includes(searchLower)
        );
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDefs, searchLower]);

  const totalMatches = grouped.reduce((sum, g) => sum + g.items.length, 0) + uncategorised.length;

  return (
    <div className={`w-full ${maxWidthClass} mx-auto my-4 px-4`}>
      {showContextHeader && (
        <header className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Definitions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Every metric in Luminoso explained in plain English, organised by learning level.
          </p>
          {(selectedCompany || selectedQuarter) && (
            <p className="text-sm text-gray-500 mt-1">
              Company: <span className="font-medium text-gray-700">{selectedCompany || '—'}</span>{' '}
              · Quarter: <span className="font-medium text-gray-700">{selectedQuarter || '—'}</span>
            </p>
          )}
        </header>
      )}

      {/* ── Search bar ── */}
      <div className="relative mb-5">
        <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
          🔎
        </span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search definitions… e.g. ROIC, cash flow, accruals"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {searchLower && (
        <p className="text-xs text-gray-400 mb-3">
          {totalMatches} definition{totalMatches !== 1 ? 's' : ''} matching "{search}"
        </p>
      )}

      {/* ── Grouped sections ── */}
      <div className="space-y-4">
        {grouped.map((group) => {
          if (group.items.length === 0) return null;
          const isCollapsed = collapsed[group.level] && !searchLower;

          // Split items into unlocked and locked for this group
          const unlockedItems = group.items.filter((item) => !lockedKeys.has(item.key));
          const lockedItems = group.items.filter((item) => lockedKeys.has(item.key));
          const hasLockedItems = lockedItems.length > 0;

          return (
            <div
              key={group.level}
              className={`rounded-xl border ${group.border} overflow-hidden`}
            >
              {/* Section header */}
              <button
                onClick={() => toggle(group.level)}
                className={`w-full flex items-center gap-3 px-5 py-3 ${group.bg} hover:brightness-95 transition-all text-left`}
              >
                <span
                  className={`w-8 h-8 rounded-full ${group.badge} text-white flex items-center justify-center font-bold text-sm flex-shrink-0`}
                >
                  {group.level}
                </span>
                <span className="text-lg">{group.icon}</span>
                <div className="flex-1 min-w-0">
                  <span className={`font-bold ${group.text}`}>{group.title}</span>
                  <span className="text-gray-400 text-xs ml-2">
                    ({group.items.length} definition{group.items.length !== 1 ? 's' : ''})
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {group.subtitle}
                  </p>
                </div>
                <span className="text-gray-400 text-sm flex-shrink-0">
                  {isCollapsed ? '▸' : '▾'}
                </span>
              </button>

              {/* Definitions list */}
              {!isCollapsed && (
                <div className="divide-y divide-gray-100 bg-white">
                  {/* Unlocked definitions — shown normally */}
                  {unlockedItems.map((item) => (
                    <div key={item.key} className="px-5 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <LevelBadge level={group.level} />
                        <span className="text-sm font-semibold text-gray-800">
                          {item.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed pl-7">
                        {item.text}
                      </p>
                    </div>
                  ))}

                  {/* Locked definitions — blurred with upgrade prompt */}
                  {hasLockedItems && (
                    <div className="relative">
                      <div className="divide-y divide-gray-100 blur-sm select-none pointer-events-none" aria-hidden="true">
                        {lockedItems.map((item) => (
                          <div key={item.key} className="px-5 py-3">
                            <div className="flex items-center gap-2 mb-1">
                              <LevelBadge level={group.level} />
                              <span className="text-sm font-semibold text-gray-800">
                                {item.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed pl-7">
                              {item.text}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70">
                        <span className="text-2xl mb-2">🔒</span>
                        <p className="text-sm font-semibold text-purple-700 mb-1">
                          {lockedItems.length} more visualisation{lockedItems.length !== 1 ? 's' : ''} available on upgrade
                        </p>
                        <button
                          className="bg-purple-600 text-white text-xs font-semibold px-4 py-2 rounded-md hover:bg-purple-700 transition-colors mt-1"
                          onClick={() => navigate('/subscribe')}
                        >
                          Upgrade
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Uncategorised (if any new definitions aren't mapped yet) */}
        {uncategorised.length > 0 && (
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggle('other')}
              className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 hover:brightness-95 transition-all text-left"
            >
              <span className="w-8 h-8 rounded-full bg-gray-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                +
              </span>
              <div className="flex-1">
                <span className="font-bold text-gray-700">Other</span>
                <span className="text-gray-400 text-xs ml-2">
                  ({uncategorised.length})
                </span>
              </div>
              <span className="text-gray-400 text-sm">
                {collapsed['other'] && !searchLower ? '▸' : '▾'}
              </span>
            </button>
            {!(collapsed['other'] && !searchLower) && (
              <div className="divide-y divide-gray-100 bg-white">
                {uncategorised.map((item) => (
                  <div key={item.key} className="px-5 py-3">
                    <span className="text-sm font-semibold text-gray-800">
                      {item.label}
                    </span>
                    <p className="text-sm text-gray-600 leading-relaxed mt-1">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {totalMatches === 0 && searchLower && (
          <div className="text-center py-8 text-gray-400">
            <p className="text-lg mb-1">No definitions match "{search}"</p>
            <p className="text-sm">Try a different keyword or clear the search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DefinitionText;

